import { ClickAwayListener } from "@mui/material";
import { findFirstNonEmptyString } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { type FunctionComponent, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { useArticleRenderContext } from "~/components/article/article-render/contexts";
import { useArticleSpeechInlineWidgetContext } from "~/components/article/article-speech/article-speech-inline-widget/contexts";
import { useArticleSpeechButton } from "~/components/article/article-speech/article-speech-inline-widget/hooks";
import type { VariantProps } from "~/components/article/article-speech/article-speech-inline-widget/variants/types";
import { useArticleSpeechContext } from "~/components/article/article-speech/contexts";
import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import { config } from "~/data/configs";
import { accountAtom } from "~/lib/account";
import type { genericArticleSpeechInlineWidgetGenericArticle$key } from "~/queries/__generated__/genericArticleSpeechInlineWidgetGenericArticle.graphql";

import { ArticleSpeechInlineWidgetGenericButton } from "./button";
import { NumberOfCharacterShow } from "./consts";
import { EmptyButton } from "./empty-button";
import {
  useAutoCollapseEffect,
  useEnableGenericInlineWidgetByUser,
  useShowGenericInlineWidgetByArticle,
} from "./hooks";
import {
  ClickAwayListenerWrapper,
  Container,
  Link,
  LogInText,
  StyledClose,
  StyledTooltip,
  Text,
  ToolTipMessage,
  Wrapper,
} from "./styles";

type Props = {
  isMinify?: boolean;
  reference: genericArticleSpeechInlineWidgetGenericArticle$key;
} & VariantProps;

export const ArticleSpeechInlineWidgetGeneric: FunctionComponent<Props> = ({
  className,
  isMinify = false,
  reference,
}) => {
  const article = useFragment(
    graphql`
      fragment genericArticleSpeechInlineWidgetGenericArticle on Article {
        ...hooksShowGenericInlineWidgetByArticleArticle
        entityId
        socialHeadline
        headline
      }
    `,
    reference,
  );
  const { articleSpeechifyPlayerConfig, setArticleSpeechifyPlayerConfig } =
    useArticleSpeechContext();
  const { articleElementReference } = useArticleRenderContext();
  const { isPlayed, onClick } = useArticleSpeechInlineWidgetContext();
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { isWidgetVisibleByType } = useShowGenericInlineWidgetByArticle(article);
  const { isWidgetEnabledByUser } = useEnableGenericInlineWidgetByUser();
  const [isTooltipActive, setIsTooltipActive] = useState(false);
  const { setIsClicked } = useArticleSpeechButton();
  const { isLoggedIn } = useAtomValue(accountAtom);

  const handleClick = () => {
    if (isPlayed || articleSpeechifyPlayerConfig?.rootElement === articleElementReference?.current)
      return;
    else if (!isWidgetEnabledByUser) {
      sendGATracking({
        action: "Subscription Entry Point/TTS/Impression",
        category: "Subscription Entry Point",
        label: window.location.href,
      });

      sendGA4Tracking({
        action: "imp",
        category: "subs",
        customized_parameters: {
          article_id: article.entityId,
          cta_type: "subscribe",
          trigger_point: "text to speech",
        },
        subcategory: "entry_point",
      });

      setIsTooltipActive(true);
      return;
    }

    onClick?.();
    setArticleSpeechifyPlayerConfig?.({
      bottomCTASettings: {
        customText: `${findFirstNonEmptyString(article.socialHeadline, article.headline).slice(
          0,
          NumberOfCharacterShow,
        )}...`,
        linkEnabled: false,
      },
      rootElement: articleElementReference?.current ?? undefined,
    });
    setIsClicked();
  };
  useAutoCollapseEffect();

  const handleLoginClick = () => {
    sendGATracking({
      action: "Subscription Entry Point/TTS/Sign in/Click",
      category: "Subscription Entry Point",
      label: window.location.href,
    });

    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        article_id: article.entityId,
        cta_type: "sign in",
        trigger_point: "text to speech",
      },
      subcategory: "entry_point",
    });

    openLoginDialog({
      description: "Sign in to enjoy our audio option, only for subscribers.",
      destination: window.location.href,
      ga4CustomParameter: {
        trigger_point: "text to speech",
      },
      title: "Listen to this article",
    });
  };

  const handleSubscriptionClickEvent = () => {
    sendGATracking({
      action: "Subscription Entry Point/TTS/Subscribe/Click",
      category: "Subscription Entry Point",
      label: window.location.href,
    });

    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        article_id: article.entityId,
        cta_type: "subscribe",
        trigger_point: "text to speech",
      },
      subcategory: "entry_point",
    });
  };

  return (
    <Wrapper $isMinify={isMinify}>
      {isWidgetVisibleByType ? (
        isWidgetEnabledByUser === undefined ? (
          <EmptyButton className={className} isMinify={isMinify} />
        ) : (
          <Container className={className} onClick={() => handleClick()}>
            <ClickAwayListener onClickAway={() => setIsTooltipActive(false)}>
              <ClickAwayListenerWrapper>
                <StyledTooltip
                  open={isTooltipActive}
                  placement="top"
                  title={
                    <ToolTipMessage>
                      <Text>
                        Our audio option is only available for subscribers.{" "}
                        {!isLoggedIn && (
                          <>
                            <LogInText onClick={() => handleLoginClick()}>LOG IN</LogInText> or{" "}
                          </>
                        )}
                        <Link
                          href={`${config.rosetta.subscribeHost}`}
                          onClick={() => handleSubscriptionClickEvent()}
                        >
                          SUBSCRIBE NOW
                        </Link>
                      </Text>
                      <StyledClose
                        onClick={event => {
                          setIsTooltipActive(false);
                          event?.stopPropagation();
                        }}
                      />
                    </ToolTipMessage>
                  }
                  arrow
                >
                  <ArticleSpeechInlineWidgetGenericButton isMinify={isMinify} />
                </StyledTooltip>
              </ClickAwayListenerWrapper>
            </ClickAwayListener>
          </Container>
        )
      ) : null}
    </Wrapper>
  );
};

ArticleSpeechInlineWidgetGeneric.displayName = "ArticleSpeechInlineWidgetGeneric";
